import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { ArrowLeft, X, Plus } from "lucide-react";
import ImageUploader from "./ImageUploader";
import VideoUploader from "./VideoUploader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProductForm() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const { eventId, productId } = useParams();
  const isEdit = !!productId;

  const [event, setEvent] = useState(null);
  const [form, setForm] = useState({
    brand: "",
    name: "",
    retailPrice: "",
    salePrice: "",
    image: "",
    description: "",
    sizes: [],
    views: "0",
    likes: "0",
    eventTitle: "",
    objectPosition: "center center",
    mediaType: "image",
    videoUrl: "",
  });
  const [newSize, setNewSize] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventRes = await fetch(`${API_URL}/api/admin/events/${eventId}`, {
          headers: { Authorization: `Bearer ${admin.token}` },
        });
        const eventData = await eventRes.json();
        setEvent(eventData);

        if (isEdit) {
          const prodRes = await fetch(
            `${API_URL}/api/admin/events/${eventId}/products`,
            { headers: { Authorization: `Bearer ${admin.token}` } }
          );
          const products = await prodRes.json();
          const product = products.find((p) => p.id === productId);
          if (product) {
            setForm({
              brand: product.brand || "",
              name: product.name || "",
              retailPrice: String(product.retailPrice || 0),
              salePrice: String(product.salePrice || 0),
              image: product.image || "",
              description: product.description || "",
              sizes: product.sizes || [],
              views: String(product.views || 0),
              likes: String(product.likes || 0),
              eventTitle: product.eventTitle || "",
              objectPosition: product.objectPosition || "center center",
              mediaType: product.mediaType || "image",
              videoUrl: product.videoUrl || "",
            });
          }
        } else {
          const eventTitle = `${eventData.badge ? eventData.badge + " " : ""}${eventData.brand}`.trim();
          setForm((prev) => ({
            ...prev,
            brand: eventData.brand || "",
            eventTitle,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [admin.token, eventId, productId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        brand: form.brand,
        name: form.name,
        retailPrice: parseFloat(form.retailPrice) || 0,
        salePrice: parseFloat(form.salePrice) || 0,
        image: form.image,
        description: form.description,
        sizes: form.sizes,
        views: parseInt(form.views) || 0,
        likes: parseInt(form.likes) || 0,
        eventTitle: form.eventTitle,
        objectPosition: form.objectPosition,
        mediaType: form.mediaType,
        videoUrl: form.mediaType === "video" ? form.videoUrl : null,
      };

      const url = isEdit
        ? `${API_URL}/api/admin/products/${productId}`
        : `${API_URL}/api/admin/events/${eventId}/products`;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Save failed");
      navigate(`/admin/events/${eventId}/products`);
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const addSize = () => {
    const size = newSize.trim().toUpperCase();
    if (size && !form.sizes.includes(size)) {
      setForm((prev) => ({ ...prev, sizes: [...prev.sizes, size] }));
      setNewSize("");
    }
  };

  const removeSize = (size) => {
    setForm((prev) => ({
      ...prev,
      sizes: prev.sizes.filter((s) => s !== size),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-product-form" className="max-w-2xl">
      <button
        onClick={() => navigate(`/admin/events/${eventId}/products`)}
        className="flex items-center gap-2 text-[#888] hover:text-white text-sm font-sans mb-6 transition-colors"
        data-testid="back-to-products"
      >
        <ArrowLeft size={16} />
        Back to {event?.brand} Products
      </button>

      <h1 className="text-white text-2xl font-sans font-semibold mb-6">
        {isEdit ? "Edit Product" : "Add Product"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Brand *
            </label>
            <input
              type="text"
              value={form.brand}
              onChange={(e) => update("brand", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              required
              data-testid="input-brand"
            />
          </div>
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Product Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              required
              data-testid="input-name"
            />
          </div>
        </div>

        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Event Title (shown in product feed header)
          </label>
          <input
            type="text"
            value={form.eventTitle}
            onChange={(e) => update("eventTitle", e.target.value)}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
            placeholder="e.g. FLASH SALE MARC JACOBS"
            data-testid="input-eventTitle"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Retail Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.retailPrice}
              onChange={(e) => update("retailPrice", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              data-testid="input-retailPrice"
            />
          </div>
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Sale Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.salePrice}
              onChange={(e) => update("salePrice", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              data-testid="input-salePrice"
            />
          </div>
        </div>

        {/* Media Type Toggle */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Media Type
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => update("mediaType", "image")}
              className={`px-4 py-2.5 rounded-lg text-sm font-sans transition-colors ${
                form.mediaType === "image"
                  ? "bg-[#D8C08F] text-black font-medium"
                  : "bg-[#1a1a1a] border border-[#2a2a2a] text-[#888] hover:text-white"
              }`}
              data-testid="media-type-image"
            >
              Image
            </button>
            <button
              type="button"
              onClick={() => update("mediaType", "video")}
              className={`px-4 py-2.5 rounded-lg text-sm font-sans transition-colors ${
                form.mediaType === "video"
                  ? "bg-[#D8C08F] text-black font-medium"
                  : "bg-[#1a1a1a] border border-[#2a2a2a] text-[#888] hover:text-white"
              }`}
              data-testid="media-type-video"
            >
              Video
            </button>
          </div>
        </div>

        {/* Product Image (primary for image type, fallback for video type) */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            {form.mediaType === "video" ? "Fallback Image (optional)" : "Product Image"}
          </label>
          <ImageUploader
            currentImage={form.image}
            onImageChange={(url) => update("image", url)}
          />
        </div>

        {/* Video Upload — only shown for video media type */}
        {form.mediaType === "video" && (
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Product Video
            </label>
            <VideoUploader
              currentVideo={form.videoUrl}
              onVideoChange={(url) => update("videoUrl", url)}
            />
          </div>
        )}

        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={3}
            className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors resize-none"
            data-testid="input-description"
          />
        </div>

        {/* Sizes */}
        <div>
          <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
            Sizes
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.sizes.map((size) => (
              <span
                key={size}
                className="inline-flex items-center gap-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-1.5 text-white text-xs font-sans"
              >
                {size}
                <button
                  type="button"
                  onClick={() => removeSize(size)}
                  className="text-[#555] hover:text-red-400"
                  data-testid={`remove-size-${size}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSize}
              onChange={(e) => setNewSize(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSize())}
              className="flex-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-2.5 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              placeholder="Add size (e.g. S, M, L, OS)"
              data-testid="input-new-size"
            />
            <button
              type="button"
              onClick={addSize}
              className="px-3 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-[#888] hover:text-white transition-colors"
              data-testid="add-size-button"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Views Count
            </label>
            <input
              type="number"
              min="0"
              value={form.views}
              onChange={(e) => update("views", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              data-testid="input-views"
            />
          </div>
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Likes Count
            </label>
            <input
              type="number"
              min="0"
              value={form.likes}
              onChange={(e) => update("likes", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              data-testid="input-likes"
            />
          </div>
        </div>

        {/* Image Position — only relevant for image products */}
        {form.mediaType === "image" && (
          <div>
            <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase tracking-wider">
              Image Position
            </label>
            <select
              value={form.objectPosition}
              onChange={(e) => update("objectPosition", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F] transition-colors"
              data-testid="input-objectPosition"
            >
              <option value="center center">Center</option>
              <option value="center 30%">Top Third</option>
              <option value="center 55%">Lower Center</option>
              <option value="center top">Top</option>
              <option value="center bottom">Bottom</option>
            </select>
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#D8C08F] text-black font-semibold px-6 py-3 rounded-lg text-sm font-sans hover:bg-[#c5ad7d] transition-colors disabled:opacity-50"
            data-testid="save-product-button"
          >
            {saving ? "Saving..." : isEdit ? "Update Product" : "Add Product"}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/events/${eventId}/products`)}
            className="bg-[#1a1a1a] border border-[#2a2a2a] text-[#888] font-medium px-6 py-3 rounded-lg text-sm font-sans hover:text-white transition-colors"
            data-testid="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
